import { createElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, List } from 'lucide-react'
import { getCurrentWeekString, getTodayDateString, getWeekStartDate, parseDateString, shiftNoteDate } from '../utils/noteDateUtils'
import { NotesListPanel } from './NotesListPanel'
import { NoteDescriptionEditor } from './NoteDescriptionEditor'
import { DueTodayPanel } from './DueTodayPanel'
import { CarryOverPanel } from './CarryOverPanel'
import { WeekStrip } from './WeekStrip'
import type { usePersonalNoteForDate } from '../hooks/usePersonalNoteForDate'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PhoneMenuButton } from '@/components/app/layout/PhoneMenuButton'
import { usePageLayout } from '@/hooks/usePageLayout'
import { useIsMobile } from '@/hooks/useMobile'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import useLoginStore from '@/store/LoginStore'
import { useTimezone } from '@/store/UserPreferencesStore'
import TaskSidebar from '@/components/app/sidebars/task'

interface PersonalNotePageLayoutProps {
  noteType: 'daily' | 'weekly'
  /** yyyy-MM-dd (daily) or yyyy-Www (weekly). */
  noteDate: string
  title: string
  isCurrentPeriod: boolean
  currentPeriodLabelKey: 'today' | 'thisWeek'
  listTitleKey: 'dailyNotes' | 'weeklyNotes'
  noteState: ReturnType<typeof usePersonalNoteForDate>
}

/**
 * A note is a doc (design 10a): the notes pane (calendar + recent) beside a
 * paper sheet with the same editor as docs. The date is the title.
 */
export function PersonalNotePageLayout({
  noteType,
  noteDate,
  title,
  isCurrentPeriod,
  currentPeriodLabelKey,
  listTitleKey,
  noteState,
}: PersonalNotePageLayoutProps) {
  const { t } = useTranslation('notes')
  const { date: formatDate } = useDateTimeFormatters()
  const isMobile = useIsMobile()
  const [isListOpen, setIsListOpen] = useState(false)
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const timezone = useTimezone()

  usePageLayout({
    sidebarContent: createElement(TaskSidebar),
  })

  const noteRoute =
    noteType === 'daily'
      ? ('/$workspaceSlug/notes/daily/$noteDate' as const)
      : ('/$workspaceSlug/notes/weekly/$noteDate' as const)
  const todayKey = noteType === 'daily' ? getTodayDateString(timezone) : getCurrentWeekString(timezone)
  // No writing ahead: a future day/week (reachable only by URL) is read-only,
  // and the next arrow stops at the current period. Both keys sort as strings.
  const isFuturePeriod = noteDate > todayKey

  // Eyebrow + big title: "DAILY NOTE · SUNDAY" / "September 27".
  const day = noteType === 'daily' ? parseDateString(noteDate) : null
  const weekStart = noteType === 'weekly' ? getWeekStartDate(noteDate) : null
  const eyebrow = day
    ? t('dailyEyebrow', { weekday: formatDate(day, { weekday: 'long' }) })
    : t('weeklyEyebrow', { week: Number(noteDate.split('-W')[1] ?? '') })
  const heading = day
    ? formatDate(day, { month: 'long', day: 'numeric' })
    : weekStart
      ? `${formatDate(weekStart, { month: 'short', day: 'numeric' })} – ${formatDate(new Date(weekStart.getTime() + 6 * 864e5), { month: 'short', day: 'numeric' })}`
      : title

  const navBar = (
    <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-chrome px-3 md:px-4 phone:h-14">
      <PhoneMenuButton className="ml-0" />
      {isMobile && (
        <Button variant="ghost" size="icon" className="size-8" onClick={() => setIsListOpen(true)}>
          <List className="size-4" />
        </Button>
      )}
      <nav className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
        <span>{t('notes')}</span>
        <span>/</span>
        <span>{t(noteType)}</span>
        <span>/</span>
        <span className="truncate font-medium text-foreground">{title}</span>
      </nav>
      <div className="ml-auto flex items-center gap-0.5">
        <Button variant="ghost" size="icon" className="size-7" asChild>
          <Link
            to={noteRoute}
            params={{ workspaceSlug, noteDate: shiftNoteDate(noteType, noteDate, -1) }}
            aria-label={noteType === 'daily' ? t('previousDay') : t('previousWeek')}
          >
            <ChevronLeft className="size-4" />
          </Link>
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" asChild disabled={isCurrentPeriod}>
          <Link to={noteRoute} params={{ workspaceSlug, noteDate: todayKey }}>
            {t('goToToday')}
          </Link>
        </Button>
        {isCurrentPeriod || isFuturePeriod ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled
            aria-label={noteType === 'daily' ? t('nextDay') : t('nextWeek')}
          >
            <ChevronRight className="size-4" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="size-7" asChild>
            <Link
              to={noteRoute}
              params={{ workspaceSlug, noteDate: shiftNoteDate(noteType, noteDate, 1) }}
              aria-label={noteType === 'daily' ? t('nextDay') : t('nextWeek')}
            >
              <ChevronRight className="size-4" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  )

  const editorContent = (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      {navBar}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 md:px-6 md:py-6">
        <article className="mx-auto min-h-full w-full max-w-[720px] rounded-xl border bg-card px-6 pb-10 pt-10 shadow-xs md:px-16">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {eyebrow}
            {isCurrentPeriod && (
              <span className="ml-2 rounded bg-brand-tint px-1.5 py-px text-[10.5px] normal-case tracking-normal text-brand-tint-foreground">
                {t(currentPeriodLabelKey)}
              </span>
            )}
          </p>
          <h1 className="mb-6 text-[34px] font-bold leading-tight tracking-[-0.02em]">{heading}</h1>
          {noteType === 'weekly' && <WeekStrip weekKey={noteDate} />}
          {noteType === 'daily' && isCurrentPeriod && <DueTodayPanel />}
          {noteType === 'daily' && isCurrentPeriod && <CarryOverPanel noteDate={noteDate} noteState={noteState} />}
          {noteState.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">{t('loading')}</div>
          ) : (
            <NoteDescriptionEditor
              note={noteState.note}
              content={noteState.content}
              canEdit={!isFuturePeriod}
              createNoteIfNeeded={noteState.createNoteIfNeeded}
              createPersonalNoteContent={noteState.createPersonalNoteContent}
              updatePersonalNoteContent={noteState.updatePersonalNoteContent}
            />
          )}
        </article>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <>
        <div className="flex h-full min-h-0 w-full flex-1">{editorContent}</div>
        <Sheet open={isListOpen} onOpenChange={setIsListOpen}>
          <SheetContent side="bottom" className="h-[80vh] p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>{t(listTitleKey)}</SheetTitle>
            </SheetHeader>
            <NotesListPanel noteType={noteType} onNavigate={() => setIsListOpen(false)} />
          </SheetContent>
        </Sheet>
      </>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1">
      <aside className="w-[292px] shrink-0 border-r">
        <NotesListPanel noteType={noteType} />
      </aside>
      {editorContent}
    </div>
  )
}
